import React from 'react'
import { Button, Card, CardTitle, DeleteIcon, Input } from 'components'
import { Add } from '@mui/icons-material'
import Dropdown from '../../../../../../components/ui/Dropdown/Dropdown'
import Box from '@mui/material/Box'
import {
  Body,
  ButtonContainer,
  HeaderSection,
  HeaderTitle,
  RowItem,
  RowsSection,
} from './Limitations.styled'
import { useQuery } from 'react-query'
import { IconButton, useTheme } from '@mui/material'
import { useBinsService, useBlendingService } from 'services'
import { useFormContext, Controller } from 'react-hook-form'

export const Limitations = () => {
  const { getLimitations } = useBlendingService()
  const { getBins } = useBinsService()
  const theme = useTheme()

  const { data: bins } = useQuery({
    queryKey: ['bins-list'],
    queryFn: () => getBins(),
  })

  const { data: blendLimitations } = useQuery({
    queryKey: ['blend-limitations'],
    queryFn: () => getLimitations(),
  })

  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors, dirtyFields },
  } = useFormContext()

  const limitations = watch('limitations', [])

  const binOptions =
    bins?.results
      ?.filter((bin) => Number(bin.code) >= 0)
      .map((bin) => ({
        value: bin.id?.toString(),
        label: bin.name,
      })) || []

  const blendLimitationOptions =
    blendLimitations?.results?.map((limitation) => ({
      value: limitation.id?.toString(),
      label: limitation.name,
    })) || []

  const handleAddRow = () => {
    setValue('limitations', [
      ...limitations,
      { bin_id: '', limitation_id: '', value: '' },
    ])
  }

  const handleDeleteRow = (index: number) => {
    const updatedLimitations = getValues('limitations').filter(
      (_: any, i: number) => i !== index
    )
    setValue('limitations', updatedLimitations)
  }

  return (
    <Card p="16px">
      <CardTitle title="Limitations" />
      <Body overflow="auto">
        {/*<LabelSection>*/}
        {/*  <HeaderLabel>*/}
        {/*    <Radio size="small" disabled readOnly /> Include external sites*/}
        {/*  </HeaderLabel>*/}
        {/*</LabelSection>*/}
        <HeaderSection>
          <Box width="177px">
            <HeaderTitle>Bins</HeaderTitle>
          </Box>
          <Box width="177px">
            <HeaderTitle>Limitations</HeaderTitle>
          </Box>
          <Box width="99px">
            <HeaderTitle>Amount</HeaderTitle>
          </Box>
        </HeaderSection>
        <RowsSection>
          {limitations.map((_: any, index: number) => {
            const isBinDirty = !!dirtyFields?.limitations?.[index]?.bin_id
            const isLimitationDirty =
              !!dirtyFields?.limitations?.[index]?.limitation_id
            const isValueDirty = !!dirtyFields?.limitations?.[index]?.value

            return (
              <RowItem key={index}>
                <Box width="177px">
                  <Controller
                    name={`limitations.${index}.bin_id`}
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        {...field}
                        label="Bins"
                        options={binOptions}
                        size="small"
                        value={field.value?.toString() || ''}
                        error={!!errors?.limitations?.[index]?.bin_id}
                        helperText={
                          errors?.limitations?.[index]?.bin_id?.message
                        }
                        sx={{
                          background: isBinDirty
                            ? theme.palette.custom.inputChanged
                            : 'none',
                        }}
                      />
                    )}
                  />
                </Box>
                <Box width="177px">
                  <Controller
                    name={`limitations.${index}.limitation_id`}
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        {...field}
                        label="Limitations"
                        options={blendLimitationOptions}
                        size="small"
                        value={field.value?.toString() || ''}
                        error={!!errors?.limitations?.[index]?.limitation_id}
                        helperText={
                          errors?.limitations?.[index]?.limitation_id?.message
                        }
                        sx={{
                          background: isLimitationDirty
                            ? theme.palette.custom.inputChanged
                            : 'none',
                        }}
                      />
                    )}
                  />
                </Box>
                <Box width="99px">
                  <Controller
                    name={`limitations.${index}.value`}
                    control={control}
                    defaultValue=""
                    render={({ field }) => {
                      const handleKeyDown = (e) => {
                        if (
                          e.key === '-' ||
                          e.key === 'e' ||
                          e.key === '+' ||
                          e.key === '.'
                        ) {
                          e.preventDefault()
                        }
                      }

                      const handleInputChange = (e) => {
                        let value = e.target.value

                        if (value === '') {
                          field.onChange('')
                          return
                        }

                        const numValue = parseInt(value, 10)
                        if (!isNaN(numValue)) {
                          if (numValue < 0) {
                            field.onChange('0')
                          } else if (numValue > 100) {
                            field.onChange('100')
                            e.target.value = '100'
                          } else {
                            field.onChange(numValue.toString())
                          }
                        }
                      }

                      const handlePaste = (e) => {
                        const pasteData = e.clipboardData.getData('text')
                        const numValue = parseInt(pasteData, 10)

                        if (!isNaN(numValue)) {
                          if (numValue < 0 || numValue > 100) {
                            e.preventDefault()
                            const limitedValue = Math.max(
                              0,
                              Math.min(100, numValue)
                            )
                            field.onChange(limitedValue.toString())
                          }
                        }
                      }

                      return (
                        <Input
                          {...field}
                          placeholder="Amount"
                          size="small"
                          type="number"
                          inputProps={{
                            min: 0,
                            max: 100,
                          }}
                          onKeyDown={handleKeyDown}
                          onChange={handleInputChange}
                          onPaste={handlePaste}
                          error={!!errors?.limitations?.[index]?.value}
                          helperText={
                            errors?.limitations?.[index]?.value?.message
                          }
                          sx={{
                            background: isValueDirty
                              ? theme.palette.custom.inputChanged
                              : 'none',
                          }}
                        />
                      )
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    fontWeight: 'bold',
                  }}
                >
                  %
                </Box>
                <ButtonContainer>
                  <IconButton onClick={() => handleDeleteRow(index)}>
                    <DeleteIcon size={20} />
                  </IconButton>
                </ButtonContainer>
              </RowItem>
            )
          })}
        </RowsSection>
        <Button width="58px" variantType="secondary" onClick={handleAddRow}>
          <Add />
        </Button>
      </Body>
    </Card>
  )
}
